/**
 * The generic webpixel wishlist click tracker only recognises a click on
 * `.wkh-button` that gains the `wk-selected` class. SNS's PLP/search wishlist
 * flow (variant picker + Omneo list picker, see ProductWishlist.js /
 * AccountWishlistModal.js) never renders that markup, so those adds are
 * invisible to it. This pushes the same `save_product` shape directly via the
 * shared BCDataLayerManager, built from the SearchSpring result already in
 * scope at the point of add rather than looking it up in window.dataLayer.
 */
const SKU_SEPARATOR = window._BCDataLayer?.config?.skuseperator || '_'

function getPageListContext() {
  const page = window.BCDataLayerManager?.manager?.page
  const data = page?.getData?.() || {}
  return {
    item_list_id: data.page_type && data.handle ? `${data.page_type}-${data.handle}` : undefined,
    item_list_name: data.handle
  }
}

function buildGTMItem(result, variantId) {
  const rawSku = result?.ss_sku != null ? String(result.ss_sku) : undefined

  const item = {
    item_id: rawSku ? rawSku.split(SKU_SEPARATOR).slice(0, 2).join(SKU_SEPARATOR) : undefined,
    item_name: result?.name,
    item_brand: result?.brand,
    item_category: result?.product_type,
    price: result?.price !== undefined ? parseFloat(result.price) : undefined,
    product_id: result?.uid !== undefined ? parseInt(result.uid, 10) : undefined,
    variant_id: variantId !== undefined ? parseInt(variantId, 10) : undefined,
    ...getPageListContext()
  }

  return Object.fromEntries(
    Object.entries(item).filter(([, value]) => value !== undefined && value !== null && !Number.isNaN(value))
  )
}

/**
 * @param {object} params
 * @param {object} params.result - SearchSpring normalised product result
 * @param {string|number} [params.variantId]
 * @param {{id: string|number, name: string}} [params.wishlist] - Omneo list the item was saved to (logged-in flow only)
 */
export function pushWishlistSaveEvent({ result, variantId, wishlist }) {
  const manager = window.BCDataLayerManager?.manager
  if (!manager || !result) return

  const item = buildGTMItem(result, variantId)
  if (!item.product_id) return

  manager.push('save_product', {
    ecommerce: {
      currency: manager.site?.getData?.()?.currency || 'AUD',
      save: {
        value: item.price,
        items: [item]
      }
    },
    ...(wishlist ? { wishlist_list_id: wishlist.id, wishlist_list_name: wishlist.name } : {})
  })
}

/**
 * The logged-in Omneo list-picker (AccountWishlistModal.js) already dispatches
 * `omneo:wishlist:update` with a `wishlistActions` detail for its own UI sync
 * (see useWishlist.js). Tapping into that here instead of calling
 * pushWishlistSaveEvent() directly from the submit handler keeps this tracker
 * decoupled from that component - any future add-to-wishlist surface that
 * dispatches the same shape gets tracked for free.
 *
 * Requires each action to carry `result` (SearchSpring product) and
 * `variantId`, which AccountWishlistModal.js includes precisely for this.
 * Note: AccountWishlist.js / AccountWishlistPDP.js also dispatch this event,
 * but as a bare Event with no detail (e.g. after a refetch) - those are
 * ignored here since they carry no wishlistActions.
 *
 * This theme's webpack build has no shared/vendor chunk across its section
 * entry points (e.g. translations.js is compiled into theme.js,
 * snippets-account.js, section-omneo-wishlist.js etc independently), so any
 * module imported from more than one entry runs its top-level code once per
 * bundle. ProductWishlist.js (which imports this file) is one of those
 * shared modules, so without this guard the listener below gets registered
 * more than once - each as a distinct function reference the browser can't
 * dedupe - and a single dispatch pushes save_product multiple times.
 */
if (!window.__bcWishlistTrackingBound) {
  window.__bcWishlistTrackingBound = true

  document.addEventListener('omneo:wishlist:update', (event) => {
    const wishlistActions = event.detail?.wishlistActions
    if (!Array.isArray(wishlistActions)) return

    wishlistActions
      .filter((action) => action.action === 'add' && action.result)
      .forEach((action) => {
        pushWishlistSaveEvent({
          result: action.result,
          variantId: action.variantId,
          wishlist: action.list ? { id: action.list.id, name: action.list.name } : undefined
        })
      })
  })
}
